import { useEffect, useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useUserActivityStore } from '../store/userActivityStore';

const IS_DEV = process.env.NODE_ENV === 'development';

export const useUserActivity = (options?: { idleTimeoutMs?: number }) => {
  const { isIdle, lastInteractionAt, idleTimeoutMs } = useUserActivityStore((state) => ({
    isIdle: state.isIdle,
    lastInteractionAt: state.lastInteractionAt,
    idleTimeoutMs: state.idleTimeoutMs,
  }));
  const setIdleTimeoutMs = useUserActivityStore((state) => state.setIdleTimeoutMs);
  const updateActivity = useUserActivityStore((state) => state.updateActivity);
  const setIdle = useUserActivityStore((state) => state.setIdle);

  // Handle idle state changes
  const onIdle = useCallback(() => {
    const changed = setIdle();
    if (changed && IS_DEV) {
      const idleForSeconds = Math.max(0, Math.round((Date.now() - lastInteractionAt) / 1000));
      // eslint-disable-next-line no-console
      console.log(`[useUserActivity] user went idle after ${idleForSeconds}s`);
    }
  }, [setIdle, lastInteractionAt]);

  const onActive = useCallback(() => {
    const now = Date.now();
    const changed = updateActivity(now);
    if (changed && IS_DEV) {
      const idleForSeconds = Math.max(0, Math.round((now - lastInteractionAt) / 1000));
      // eslint-disable-next-line no-console
      console.log(`[useUserActivity] user became active after ${idleForSeconds}s idle`);
    }
  }, [updateActivity, lastInteractionAt]);

  // Initialize react-idle-timer
  useIdleTimer({
    timeout: idleTimeoutMs,
    onIdle,
    onActive,
    throttle: 500, // Reduced throttle for better responsiveness
    eventsThrottle: 500, // Throttle at event listener level
    crossTab: true, // Sync across tabs automatically
    syncTimers: 200, // Check other tabs every 200ms
    startManually: false,
  });

  // Update timeout if changed via options
  useEffect(() => {
    if (typeof options?.idleTimeoutMs === 'number') {
      setIdleTimeoutMs(options.idleTimeoutMs);
    }
  }, [options?.idleTimeoutMs, setIdleTimeoutMs]);

  // Manual activity recording (for programmatic use)
  const recordActivity = useCallback((timestamp?: number) => {
    const resolvedTimestamp = timestamp ?? Date.now();
    updateActivity(resolvedTimestamp);
  }, [updateActivity]);

  return {
    isIdle,
    lastInteractionAt,
    idleTimeoutMs,
    recordActivity,
  } as const;
};

export const getIsUserIdle = () => useUserActivityStore.getState().isIdle;
